import React, { FC, Fragment, useRef, useState } from 'react';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useTranslation } from 'react-i18next';
import { InvoicingState, Order } from 'models/order';
import { Box, Button, CircularProgress, Divider, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { InvoiceStatus } from 'models/invoice';
import {
    buildOrderPdfFileName,
    downloadDestinationAddressLabelPdf,
    downloadDocumentBundlePdf,
    downloadOrderPdf,
    downloadOrderReceiptPdf,
    downloadPatientLabelPdf,
} from './files';
import { useMutation, useQuery } from '@apollo/client';
import { reCreateDeliveryNoteTemplate } from 'apollo/templates/order';
import { documentBundlesTemplate } from 'apollo/templates/organization';
import { orderDeviationsTemplate } from 'apollo/templates/deviation';
import { BundleDocumentType, DocumentBundle } from 'models/organization';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type OnTheFlySpinnerKey =
    | 'showPatientSpinner'
    | 'showOrderSpinner'
    | 'showWorkSheetSpinner'
    | 'showWorkSheetOldSpinner'
    | 'showWorkSheetSubcontractorSpinner'
    | 'showOrderFormSpinner'
    | 'showDeviationReportSpinner';

/**
 * Documents rendered on demand by denthub-documents (one Chromium launch each), keyed by the
 * document type the backend expects. Replaces two parallel five-branch if/else chains that had
 * to be kept in sync between the try and the finally.
 */
const ON_THE_FLY_DOCUMENTS: Record<string, { fileNameSuffix: string; spinnerKey: OnTheFlySpinnerKey }> = {
    PATIENT_SPECIFICATION_PDF: { fileNameSuffix: '_spec.pdf', spinnerKey: 'showPatientSpinner' },
    ORDER_SUMMARY_PDF: { fileNameSuffix: '.pdf', spinnerKey: 'showOrderSpinner' },
    WORK_SHEET_V2_PDF: { fileNameSuffix: '_work.pdf', spinnerKey: 'showWorkSheetSpinner' },
    // DD-3584: the v1 work sheet, back for labs that miss it. Only offered here — not in bundles,
    // batch print or the order flows, which all use WORK_SHEET_V2_PDF.
    WORK_SHEET_PDF: { fileNameSuffix: '_work_old.pdf', spinnerKey: 'showWorkSheetOldSpinner' },
    WORK_SHEET_SUBCONTRACTOR_PDF: {
        fileNameSuffix: '_worksheet_subcontractor.pdf',
        spinnerKey: 'showWorkSheetSubcontractorSpinner',
    },
    ORDER_FORM_PDF: { fileNameSuffix: '_form.pdf', spinnerKey: 'showOrderFormSpinner' },
    DEVIATION_REPORT_PDF: { fileNameSuffix: '_deviation_report.pdf', spinnerKey: 'showDeviationReportSpinner' },
};

type DocumentPrintActionsProps = {
    order: Order;
    isExternalTechnician: boolean;
    isCustomer: boolean;
    isSupplier: boolean;
    onHandleDeliveryNoteCreation: () => void;
};
const DocumentPrintActions: FC<DocumentPrintActionsProps> = ({
    order,
    isExternalTechnician,
    isCustomer,
    isSupplier,
    onHandleDeliveryNoteCreation,
}: DocumentPrintActionsProps) => {
    const currentUser = currentUserVar();
    // DD-3659: the elevated external tier ships orders, so it gets the delivery note.
    const canDownloadDeliveryNote = !isExternalTechnician || permissions.isElevatedExternalUser(currentUser?.role);
    // DD-3781: same audience as the delivery note, which denthub-documents now enforces.
    const canPrintDestinationLabel = canDownloadDeliveryNote;
    const currentOrganization = currentOrgVar();
    const { t: translate } = useTranslation(['orders', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [error, setError] = useState<Error | null>(null);
    const [showSpinner, setSpinner] = useState({
        showDestLabelSpinner: false,
        showOrderSpinner: false,
        showPatientLabelSpinner: false,
        showPatientSpinner: false,
        showReceiptSpinner: false,
        showRecreateDeliveryNoteSpinner: false,
        showWorkSheetSpinner: false,
        showWorkSheetOldSpinner: false,
        showWorkSheetSubcontractorSpinner: false,
        showOrderFormSpinner: false,
        showDeviationReportSpinner: false,
    });
    const isExternalLabUsersEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.EXTERNAL_LAB_USERS,
    );
    // Document types with a request already in flight. A ref, not state: the guard has to be
    // readable synchronously within the same tick as the click.
    const inFlightDocumentTypes = useRef<Set<string>>(new Set());

    const [reCreateDeliveryNote] = useMutation(reCreateDeliveryNoteTemplate, {
        onCompleted: () => {
            onHandleDeliveryNoteCreation();
        },
    });

    // The documentBundles query is guarded to lab_technician users, so only fetch on the lab side.
    const { data: documentBundlesData } = useQuery(documentBundlesTemplate, {
        skip: isCustomer || isExternalTechnician,
        fetchPolicy: 'cache-and-network',
    });
    const documentBundles: DocumentBundle[] = documentBundlesData?.documentBundles ?? [];
    const [bundleSpinnerId, setBundleSpinnerId] = useState<string | null>(null);

    // orderDeviations is already server-filtered to visibleForDentist deviations.
    const { data: orderDeviationsData } = useQuery(orderDeviationsTemplate, {
        variables: { orderId: order._id },
        fetchPolicy: 'cache-and-network',
    });
    const hasDentistVisibleDeviation = (orderDeviationsData?.orderDeviations?.length ?? 0) > 0;

    const handleBundlePrint = async (bundle: DocumentBundle) => {
        try {
            setBundleSpinnerId(bundle._id || null);
            const fileName = `${order.orderId}_${bundle.title}.pdf`;
            await downloadDocumentBundlePdf(order._id, bundle.documents, fileName);
        } catch (error) {
            // UserFrontendFeedback errors render as a dialog; anything else escalates to the boundary.
            showBoundary(error);
        } finally {
            setBundleSpinnerId(null);
        }
    };

    const handlePdfPrint = async (type: string) => {
        // Leading-edge guard. `disabled` on the buttons only takes effect once React has
        // committed the state update, so a double-click inside the same tick would otherwise
        // fire two full renders — each of which launches its own Chromium on the server.
        if (inFlightDocumentTypes.current.has(type)) {
            return;
        }
        inFlightDocumentTypes.current.add(type);

        const documentConfig = ON_THE_FLY_DOCUMENTS[type];
        const fileName = buildOrderPdfFileName(order, documentConfig?.fileNameSuffix ?? '');

        // Functional updater, not `{ ...showSpinner }`: the spread captured the value from the
        // render closure, so two overlapping prints wrote conflicting snapshots and could leave
        // a spinner stuck on — or clear it while its request was still in flight.
        const setDocumentSpinner = (isVisible: boolean) => {
            if (!documentConfig) {
                return;
            }
            setSpinner((previousSpinner) => ({ ...previousSpinner, [documentConfig.spinnerKey]: isVisible }));
        };

        setDocumentSpinner(true);
        try {
            await downloadOrderPdf(order._id, type, fileName);
        } catch (error) {
            setError(error as Error);
        } finally {
            inFlightDocumentTypes.current.delete(type);
            setDocumentSpinner(false);
        }
    };

    const handlePatientLabelPdfPrint = async () => {
        try {
            setSpinner({ ...showSpinner, showPatientLabelSpinner: true });
            await downloadPatientLabelPdf(order);
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner({ ...showSpinner, showPatientLabelSpinner: false });
        }
    };

    const handleDestinationAddressLabelPdfPrint = async () => {
        try {
            setSpinner({ ...showSpinner, showDestLabelSpinner: true });
            await downloadDestinationAddressLabelPdf(order);
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner({ ...showSpinner, showDestLabelSpinner: false });
        }
    };

    const handleSignedUrlDownload = (signedUrl: string) => {
        window.open(signedUrl);
    };

    const enableInvoicePdfButton = () => {
        return (
            order.deliveryDetails?.price !== 0 &&
            !!order.invoice?.pdfUrl &&
            (order.invoice?.status === InvoiceStatus.PROCESSING ||
                order.invoice?.status === InvoiceStatus.SCHEDULED ||
                order.invoice?.status === InvoiceStatus.SENT ||
                order.invoice?.status === InvoiceStatus.CREATED ||
                order.invoice?.status === InvoiceStatus.EXTERNAL_INVOICE_ERROR)
        );
    };

    const handleReceiptPdf = async () => {
        try {
            setSpinner({ ...showSpinner, showReceiptSpinner: true });
            await downloadOrderReceiptPdf(order);
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner({ ...showSpinner, showReceiptSpinner: false });
        }
    };

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    const handleReCreateDeliveryNote = async () => {
        setSpinner({ ...showSpinner, showRecreateDeliveryNoteSpinner: true });
        await reCreateDeliveryNote({
            variables: {
                orderId: order?._id,
            },
        });
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            <List disablePadding dense>
                {!isExternalTechnician ? (
                    <ListItem disablePadding dense>
                        {showSpinner.showPatientSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showPatientSpinner}
                            onClick={() => handlePdfPrint('PATIENT_SPECIFICATION_PDF')}
                            date-test="print-actions.patient-specification"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_PATIENT_SPECIFICATION')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                <ListItem disablePadding dense>
                    {showSpinner.showOrderSpinner ? (
                        <CircularProgress
                            variant="indeterminate"
                            sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                            size={15}
                        />
                    ) : undefined}
                    <ListItemButton
                        disabled={showSpinner.showOrderSpinner}
                        onClick={() => handlePdfPrint('ORDER_SUMMARY_PDF')}
                        data-test="print-actions.order-summary"
                    >
                        <ListItemText
                            sx={{ color: 'primary.main' }}
                            primary={translate('orders:PRINT_ORDER_SUMMARY')}
                        />
                    </ListItemButton>
                </ListItem>
                {!isExternalTechnician ? (
                    <ListItem disablePadding dense>
                        {showSpinner.showOrderFormSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showOrderFormSpinner}
                            onClick={() => handlePdfPrint('ORDER_FORM_PDF')}
                            data-test="print-actions.order-form"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_ORDER_FORM')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {isExternalTechnician || (isSupplier && isExternalLabUsersEnabled) ? (
                    <ListItem disablePadding dense>
                        {showSpinner.showWorkSheetSubcontractorSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showWorkSheetSubcontractorSpinner}
                            onClick={() => handlePdfPrint('WORK_SHEET_SUBCONTRACTOR_PDF')}
                            data-test="print-actions.work-sheet-subcontractor"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_WORKSHEET_SUBCONTRACTOR')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {!isCustomer && !isExternalTechnician ? (
                    <ListItem disablePadding dense>
                        {showSpinner.showWorkSheetSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showWorkSheetSpinner}
                            onClick={() => handlePdfPrint('WORK_SHEET_V2_PDF')}
                            data-test="print-actions.work-sheet"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_WORKSHEET')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {!isCustomer && !isExternalTechnician ? (
                    <ListItem disablePadding dense>
                        {showSpinner.showWorkSheetOldSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showWorkSheetOldSpinner}
                            onClick={() => handlePdfPrint('WORK_SHEET_PDF')}
                            data-test="print-actions.work-sheet-old"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_WORKSHEET_OLD')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {canPrintDestinationLabel ? (
                    <ListItem disablePadding dense>
                        {showSpinner.showDestLabelSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showDestLabelSpinner}
                            onClick={handleDestinationAddressLabelPdfPrint}
                            data-test="print-actions.destination-label"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_DELIVERY_ADDRESS_LABEL')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {!isExternalTechnician && (
                    <ListItem disablePadding dense>
                        {showSpinner.showPatientLabelSpinner ? (
                            <CircularProgress
                                variant="indeterminate"
                                sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                size={15}
                            />
                        ) : undefined}
                        <ListItemButton
                            disabled={showSpinner.showPatientLabelSpinner}
                            onClick={handlePatientLabelPdfPrint}
                            date-test="print-actions.patient-label"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_PATIENT_LABEL')}
                            />
                        </ListItemButton>
                    </ListItem>
                )}
                <ListItem disablePadding dense>
                    {showSpinner.showReceiptSpinner ? (
                        <CircularProgress
                            variant="indeterminate"
                            sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                            size={15}
                        />
                    ) : undefined}
                    <ListItemButton
                        disabled={showSpinner.showReceiptSpinner}
                        onClick={handleReceiptPdf}
                        data-test="print-actions.order-receipt"
                    >
                        <ListItemText
                            sx={{ color: 'primary.main' }}
                            primary={translate('orders:PRINT_ORDER_RECEIPT')}
                        />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding dense>
                    {showSpinner.showDeviationReportSpinner ? (
                        <CircularProgress
                            variant="indeterminate"
                            sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                            size={15}
                        />
                    ) : undefined}
                    <ListItemButton
                        disabled={showSpinner.showDeviationReportSpinner || !hasDentistVisibleDeviation}
                        onClick={() => handlePdfPrint('DEVIATION_REPORT_PDF')}
                        data-test="print-actions.deviation-report"
                    >
                        <ListItemText
                            sx={{ color: 'primary.main' }}
                            primary={translate('orders:PRINT_DEVIATION_REPORT')}
                        />
                    </ListItemButton>
                </ListItem>
                {canDownloadDeliveryNote ? (
                    <ListItem disablePadding dense>
                        <ListItemButton
                            disabled={!order.deliveryNotePdfUrl}
                            onClick={() => handleSignedUrlDownload(order.deliveryNotePdfUrl)}
                            data-test="print-actions.delivery-note"
                        >
                            <ListItemText
                                sx={{ color: 'primary.main' }}
                                primary={translate('orders:PRINT_DELIVERY_NOTE')}
                            />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {!isExternalTechnician ? (
                    <ListItem disablePadding dense>
                        <ListItemButton
                            disabled={!enableInvoicePdfButton()}
                            onClick={() => handleSignedUrlDownload(order.invoice.pdfUrl)}
                            data-test="print-actions.invoice"
                        >
                            <ListItemText sx={{ color: 'primary.main' }} primary={translate('orders:PRINT_INVOICE')} />
                        </ListItemButton>
                    </ListItem>
                ) : undefined}
                {documentBundles.length > 0 ? (
                    <Fragment>
                        <Divider component="li" sx={{ my: 1 }} />
                        {documentBundles.map((bundle) => (
                            <ListItem disablePadding dense key={bundle._id}>
                                {bundleSpinnerId === bundle._id ? (
                                    <CircularProgress
                                        variant="indeterminate"
                                        sx={{ zIndex: 10000, position: 'absolute', top: '25%', left: '-2%' }}
                                        size={15}
                                    />
                                ) : undefined}
                                <ListItemButton
                                    disabled={bundleSpinnerId === bundle._id}
                                    onClick={() => handleBundlePrint(bundle)}
                                    data-test={`print-actions.bundle-${bundle._id}`}
                                >
                                    <ListItemText sx={{ color: 'primary.main' }} primary={bundle.title} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </Fragment>
                ) : undefined}
            </List>
            {!isCustomer && !isExternalTechnician && modifyLocked() && !order.isImportedOrder ? (
                <Fragment>
                    <Box my={1}>
                        <Divider />
                    </Box>
                    <Button
                        fullWidth
                        size="small"
                        disabled={showSpinner.showRecreateDeliveryNoteSpinner}
                        onClick={() => handleReCreateDeliveryNote()}
                        variant="outlined"
                    >
                        {translate('orders:RECREATE_DELIVERY_NOTE')}
                    </Button>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default DocumentPrintActions;
